import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useRef } from 'react';
import classes from './drugReferenceByConceptCodeTable.module.scss';
import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import searchIcon from 'assets/icons/PPSIcons/searchIcon.svg';
import { useDrugReferenceByConceptCodeTable } from './useDrugReferenceByConceptCode';
import AddRowModalDrugReferenceByConceptCode from './utils/AddRowModalDrugReferenceByConceptCode';
import DeleteConfirmationModalDrugReferenceByConceptCode from './utils/DeleteConfirmationModalDrugReferenceByConceptCode';
import { DrugReferenceByConceptCode } from 'Interfaces/ReferenceInterfaces/DrugReferenceByConceptCodeInterface';

interface DrugReferenceByConceptCodeTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const DrugReferenceByConceptCodeTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: DrugReferenceByConceptCodeTableProps) => {
    const pageSize = showAllRows ? 100000 : 100;

    const {
      drugReferenceData,
      isLoading,
      pagination,
      coatingOptions,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      handleSearchChange,
      prepareFormData,
      onPageChange,
      dosageFormOptions,
      handleExportToExcel
    } = useDrugReferenceByConceptCodeTable(pageSize);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: DrugReferenceByConceptCode) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    const coatingTemplate = (rowData: DrugReferenceByConceptCode) => {
      return rowData.coated ? rowData.coated.description : '';
    };

    // Format strength to remove trailing zeroes
    const formatStrength = (strength: number | string | null | undefined): string => {
      if (strength === null || strength === undefined || strength === '') {
        return '';
      }

      // Convert to number and back to string to remove trailing zeroes
      // const numValue = parseFloat(strength.toString());
      // if (isNaN(numValue)) {
      //   return strength.toString();
      // }

      return strength.toString();
    };

    const strengthTemplate = (rowData: DrugReferenceByConceptCode) => {
      return formatStrength(rowData.strength);
    };

    // if (isLoading && !drugReferenceData.length) {
    //   return (
    //     <div className={classes.loadingContainer}>
    //       <ProgressSpinner className={classes.spinner} />
    //     </div>
    //   );
    // }

    return (
      <div className={classes.drugReferenceByConceptCodeTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Drug Reference By Concept Code Configuration</h2>
          <div className={classes.headerActions}>
            <div className={classes.searchContainer}>
              <span className={classes.searchIconSection}>
                <img src={searchIcon} alt="Search Icon" style={{ marginRight: '0.5rem' }} />
              </span>
              <InputText
                ref={searchInputRef}
                className={classes.searchInput}
                placeholder="Search..."
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {/* {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )} */}
          </div>
        </div>

        {/* Show loading spinner only for the table content, not the entire component */}
        {isLoading && !drugReferenceData.length ? (
          <div className={classes.loadingContainer}>
            <ProgressSpinner className={classes.spinner} />
          </div>
        ) : (
          <DataTable
            value={drugReferenceData}
            showGridlines
            stripedRows
            emptyMessage="No records found"
            className={classes.dataTable}
            loading={isLoading}
            paginator={!showAllRows}
            rows={showAllRows ? drugReferenceData.length : 100}
            totalRecords={pagination.totalItems}
            lazy
            first={(pagination.currentPage - 1) * 100000}
            onPage={onPageChange}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          >
            <Column field="concept_code" header="Concept Code" />
            <Column field="proprietary_name" header="Proprietary Name" />
            <Column field="strength" header="Strength" body={strengthTemplate} />
            <Column field="dosage_form" header="Dosage Form" />
            <Column field="coated" header="Coated" body={coatingTemplate} />
            <Column field="product_color" header="Product Color" />
            {showActionColumn && (
              <Column
                body={actionBodyTemplate}
                style={{ width: '5rem', textAlign: 'center' }}
                headerStyle={{ width: '5rem' }}
              />
            )}
          </DataTable>
        )}

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalDrugReferenceByConceptCode
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
          coatingOptions={coatingOptions}
          dosageFormOptions={dosageFormOptions}
        />

        <DeleteConfirmationModalDrugReferenceByConceptCode
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default DrugReferenceByConceptCodeTable;
